/**
 * Envío AP-3 · barra de estado del timeline (vista Edición).
 *
 * Un renglón entre el preview y las pistas, arriba de la regla: lo elegido
 * (miniatura, nombre, tiempos) y sus acciones (Reemplazar, Cortar, Duplicar,
 * Silenciar, Borrar), el Imán, el guardado, las plantillas, el volumen
 * general, el zoom y Exportar. Reemplaza al encabezado del editor y al botón
 * Exportar de la barra principal.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRightLeft, Scissors, Copy, Volume2, VolumeX, Trash2, Magnet, ZoomIn, ZoomOut,
  Maximize2, Download, ChevronDown, Loader2, LayoutTemplate, BookmarkPlus, AlertTriangle, PenLine,
} from 'lucide-react';
import { requestImageEdit } from '../imageEdit/TimelineImageEdit';
import { toast } from 'sonner';
import useTimelineStore, { ZOOM_MAX, ZOOM_MIN, BASE_PX_PER_SEC } from '../../store/timelineStore';
import { describeSelection, fitZoom, fmtSecs, fmtTime } from './timelineBarModel';

export const RES_OPTIONS = [['720p', '720p HD'], ['1080p', '1080p FHD'], ['4k', '4K UHD']];

/** Borra lo elegido (clip, texto/sticker o transición) y ofrece deshacer. */
export function removeSelection(sel) {
  const st = useTimelineStore.getState();
  if (!sel) return;
  if (sel.group === 'element') { st.removeElement(sel.id); st.selectClip(null); return; }
  if (sel.group === 'transition') { st.removeTransition(sel.id); st.selectClip(null); return; }
  const out = st.removeClip(sel.trackKind, sel.id);
  if (!out) return;
  toast(`«${out.label}» borrado${out.closed ? ' · se cerró el espacio' : ''}`, {
    position: 'bottom-center',
    action: { label: 'Deshacer', onClick: () => useTimelineStore.getState().undoLast() },
  });
}

export function cutSelection(sel) {
  if (!sel?.can.cut) return;
  const st = useTimelineStore.getState();
  st.splitClip(sel.trackKind, sel.id, st.playheadTime);
}

const Btn = ({ icon: Icon, label, onClick, disabled, danger, showLabel, pressed, testid }) => (
  <button
    type="button"
    className={`tlb-btn${danger ? ' is-danger' : ''}${showLabel ? ' has-label' : ''}`}
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={showLabel ? undefined : label}
    aria-pressed={pressed}
    data-testid={testid}
  >
    <Icon size={15} aria-hidden="true" />
    {showLabel && <span>{label}</span>}
  </button>
);

export default function TimelineBar({
  onReplace, onExport, rendering, totalDuration, cost, costFor,
  viewportWidth, syncLabel, syncState,
  templates, onOpenTemplates, onSaveTemplate, canSaveTemplate, emptySlots, onOpenComplete,
}) {
  // Zustand 5: cada selector devuelve una referencia estable; la descripción se arma con useMemo.
  const selectedClip = useTimelineStore((s) => s.selectedClip);
  const videoClips = useTimelineStore((s) => s.videoClips);
  const imageClips = useTimelineStore((s) => s.imageClips);
  const overlayV1Clips = useTimelineStore((s) => s.overlayV1Clips);
  const overlayV2Clips = useTimelineStore((s) => s.overlayV2Clips);
  const audioClips = useTimelineStore((s) => s.audioClips);
  const elements = useTimelineStore((s) => s.elements);
  const videoTransitions = useTimelineStore((s) => s.videoTransitions);
  const playheadTime = useTimelineStore((s) => s.playheadTime);
  const sel = useMemo(() => describeSelection({
    selectedClip, videoClips, imageClips, overlayV1Clips, overlayV2Clips, audioClips,
    elements, videoTransitions, playheadTime,
  }), [selectedClip, videoClips, imageClips, overlayV1Clips, overlayV2Clips, audioClips,
    elements, videoTransitions, playheadTime]);
  const magnet = useTimelineStore((s) => s.magnet);
  const zoom = useTimelineStore((s) => s.zoom);
  const masterMuted = useTimelineStore((s) => s.masterMuted);
  const masterVolume = useTimelineStore((s) => s.masterVolume);
  const resolution = useTimelineStore((s) => s.resolution);
  const editingImage = useTimelineStore((s) => !!s.imageEdit);
  const [resOpen, setResOpen] = useState(false);
  const resRef = useRef(null);

  useEffect(() => {
    if (!resOpen) return undefined;
    const close = (e) => { if (resRef.current && !resRef.current.contains(e.target)) setResOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setResOpen(false); };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('pointerdown', close); window.removeEventListener('keydown', esc); };
  }, [resOpen]);

  const st = useTimelineStore.getState;
  const can = sel?.can || {};
  const resLabel = (RES_OPTIONS.find(([k]) => k === resolution) || [])[1] || resolution;

  return (
    <div className="tlb" role="toolbar" aria-label="Edición del timeline" data-testid="tl-bar">
      <div className={`tlb-main${editingImage ? ' is-locked' : ''}`} aria-disabled={editingImage || undefined}>
      <div className="tlb-sel" aria-live="polite">
        {sel ? (
          <>
            {sel.thumb
              ? <img src={sel.thumb} alt="" className="tlb-thumb" />
              : <span className={`tlb-thumb tlb-thumb-${sel.trackKind}`} aria-hidden="true" />}
            <span className="tlb-sel-txt">
              <b title={sel.label}>{sel.label}</b>
              <small>
                {sel.kindLabel}
                {sel.start != null ? ` · ${fmtTime(sel.start)} – ${fmtTime(sel.start + sel.duration)}` : ''}
                {` · ${fmtSecs(sel.duration)}`}
              </small>
            </span>
          </>
        ) : (
          <span className="tlb-empty">Seleccioná un clip para editarlo</span>
        )}
      </div>

      <Btn icon={ArrowRightLeft} label="Reemplazar" showLabel disabled={!can.replace}
        onClick={() => onReplace?.({ trackKind: sel.trackKind, id: sel.id })} testid="tlb-replace" />
      {can.editImage && (
        <Btn icon={PenLine} label={sel.edited ? 'Volver a editar' : 'Editar imagen'} showLabel
          onClick={() => requestImageEdit(sel.trackKind, sel.id)} testid="tlb-edit-image" />
      )}
      <Btn icon={Scissors} label={can.cut || !sel ? 'Cortar en el cabezal' : 'Poné el cabezal sobre el clip para cortarlo'}
        disabled={!can.cut} onClick={() => cutSelection(sel)} testid="tlb-cut" />
      <Btn icon={Copy} label="Duplicar" disabled={!can.duplicate}
        onClick={() => (sel.group === 'element' ? st().duplicateElement(sel.id) : st().duplicateClip(sel.trackKind, sel.id))}
        testid="tlb-duplicate" />
      <Btn icon={sel?.muted ? VolumeX : Volume2} label={sel?.muted ? 'Activar sonido del clip' : 'Silenciar el clip'}
        disabled={!can.mute} pressed={can.mute ? !!sel?.muted : undefined}
        onClick={() => st().updateClip(sel.trackKind, sel.id, { muted: !sel.muted })} testid="tlb-mute" />
      <Btn icon={Trash2} label="Borrar" showLabel danger disabled={!can.remove}
        onClick={() => removeSelection(sel)} testid="tlb-delete" />

      <span className="tlb-sep" aria-hidden="true" />
      <button
        type="button"
        className={`tlb-magnet ${magnet ? 'is-on' : ''}`}
        aria-pressed={magnet}
        onClick={() => st().setMagnet(!magnet)}
        title={magnet ? 'Imán prendido: los clips se pegan y al borrar no quedan huecos' : 'Imán apagado: los clips se mueven libres'}
        data-testid="tlb-magnet"
      >
        <Magnet size={15} aria-hidden="true" /> Imán
      </button>

      <span className="tlb-grow" />

      {syncLabel && (
        <span className={`tlb-sync is-${syncState}`} role="status" data-testid="tl-sync-status">
          {syncState === 'saving' ? <Loader2 size={12} className="spin" /> : <i aria-hidden="true" />}
          {syncLabel}
        </span>
      )}
      {templates && (
        <>
          <Btn icon={LayoutTemplate} label="Plantillas" onClick={onOpenTemplates} testid="tl-templates-btn" />
          <Btn icon={BookmarkPlus} label="Guardar como plantilla" onClick={onSaveTemplate}
            disabled={!canSaveTemplate} testid="tl-save-template-btn" />
          {emptySlots > 0 && (
            <button type="button" className="tlb-slots" onClick={onOpenComplete} data-testid="tl-complete-template-btn"
              title="Completar los huecos de la plantilla">
              <AlertTriangle size={12} aria-hidden="true" /> {emptySlots} hueco(s)
            </button>
          )}
        </>
      )}

      <span className="tlb-sep" aria-hidden="true" />
      <span className="tlb-vol" data-testid="tl-master-audio">
        <Btn icon={masterMuted ? VolumeX : Volume2} label={masterMuted ? 'Activar audio general' : 'Silenciar todo'}
          pressed={masterMuted} onClick={() => st().setMasterMuted(!masterMuted)} testid="tl-master-mute" />
        <input
          type="range" min={0} max={2} step={0.05} value={masterVolume}
          onChange={(e) => st().setMasterVolume(parseFloat(e.target.value))}
          aria-label="Volumen general" title={`Volumen general: ${Math.round(masterVolume * 100)} %`}
          data-testid="tl-master-volume"
        />
      </span>

      <span className="tlb-sep" aria-hidden="true" />
      <span className="tlb-zoom" data-testid="tl-zoom-group">
        <Btn icon={ZoomOut} label="Alejar" onClick={() => st().zoomOut()} testid="tl-zoom-out" />
        <button type="button" className="tlb-zoom-level" onClick={() => st().resetZoom()}
          title="Volver al 100 %" data-testid="tl-zoom-reset">{Math.round(zoom * 100)} %</button>
        <Btn icon={ZoomIn} label="Acercar" onClick={() => st().zoomIn()} testid="tl-zoom-in" />
        <Btn icon={Maximize2} label="Ajustar todo a la vista"
          onClick={() => st().setZoom(fitZoom(totalDuration, viewportWidth, BASE_PX_PER_SEC, ZOOM_MIN, ZOOM_MAX))}
          disabled={totalDuration <= 0} testid="tl-zoom-fit" />
      </span>
      </div>

      <span className="tlb-export" ref={resRef}>
        <button
          type="button"
          className="tlb-export-main"
          onClick={onExport}
          disabled={rendering || totalDuration <= 0}
          title={totalDuration <= 0 ? 'La timeline está vacía' : `Exportar en ${resLabel} · ${cost} créditos`}
          data-testid="tl-export-btn"
        >
          {rendering ? <Loader2 size={14} className="spin" /> : <Download size={14} aria-hidden="true" />}
          {rendering ? 'Renderizando…' : <>Exportar <small>· {cost} cr</small></>}
        </button>
        <button
          type="button"
          className="tlb-export-caret"
          onClick={() => setResOpen((v) => !v)}
          disabled={rendering}
          aria-haspopup="menu"
          aria-expanded={resOpen}
          aria-label={`Resolución: ${resLabel}`}
          title="Resolución y costo"
          data-testid="export-menu-btn"
        >
          <ChevronDown size={14} />
        </button>
        {resOpen && (
          <div className="tlb-menu" role="menu" data-testid="export-menu">
            <div className="tlb-menu-head">Resolución del video</div>
            {RES_OPTIONS.map(([k, label]) => (
              <button
                key={k} type="button" role="menuitemradio" aria-checked={resolution === k}
                className={resolution === k ? 'active' : ''}
                onClick={() => { st().setResolution(k); setResOpen(false); }}
                data-testid={`tl-res-${k}`}
              >
                <span>{label}</span>
                <small>{totalDuration <= 0 ? '—' : `${costFor(k)} créditos`}</small>
              </button>
            ))}
          </div>
        )}
      </span>
    </div>
  );
}
